'use client'

import { ArrowLeft } from 'lucide-react'
import Link from 'next/link'
import { useEffect } from 'react'
import { ContactField } from '@/features/contact/view/ContactField'
import { useContactForm } from '@/features/contact/view/useContactForm'
import { emailLink, routePaths } from '@/shared/config/site'
import { gtmEvent } from '@/shared/lib/googleTag'
import { Button } from '@/shared/ui/Button'
import { EmailActionButton } from '@/shared/ui/EmailActionButton'

export function ContactPage() {
  const { fieldErrors, formMessage, handleFieldChange, handleSubmit, isSubmitting, submissionState, values } =
    useContactForm()

  useEffect(() => {
    document.title = 'Contact | Vitaliy'
  }, [])

  useEffect(() => {
    gtmEvent('contact_page_view', { page_path: '/contact' })
  }, [])

  return (
    <div className="page-shell px-5 py-24 md:px-8 lg:px-10">
      <main className="mx-auto max-w-6xl" id={'content'}>
        <div className="mb-10 flex flex-wrap items-center gap-3">
          <Button asChild size="sm" variant="ghost">
            <Link href={routePaths.home}>
              <ArrowLeft className="h-4 w-4" />
              {'Back Home'}
            </Link>
          </Button>
          <Button asChild size="sm" variant="secondary">
            <Link href={routePaths.service}>{'Services'}</Link>
          </Button>
        </div>
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_28rem] lg:items-start">
          <section className="max-w-2xl">
            <h1 className="display-title text-4xl md:text-5xl">{'Contact'}</h1>
            <p className="mt-4 text-lg leading-8 text-white/70">
              {'Tell me what is broken, why it matters, and what kind of help you actually need.'}
            </p>
            <div className="mt-8 space-y-6">
              <div className="contact-panel p-6">
                <h2 className="text-base font-semibold text-phosphor-ivory">{'Prefer direct email?'}</h2>
                <p className="mt-3 text-sm leading-7 text-white/68">
                  {'Use the mail button instead. Same inbox. Less ceremony.'}
                </p>
                <EmailActionButton
                  className="mt-5"
                  copiedLabel={'Email address copied'}
                  copyLabel={'Copy email address'}
                  href={emailLink.href ?? ''}
                  label={'Email the Autist'}
                />
              </div>
              <div className="contact-panel p-6">
                <h2 className="text-base font-semibold text-phosphor-ivory">{'What helps'}</h2>
                <ul className="mt-4 space-y-3 text-sm leading-7 text-white/72">
                  {[
                    'What the problem is.',
                    'Why it matters now.',
                    'What you want me to do.',
                    'Anything useful like links, screenshots, or repo access notes.',
                  ].map((item) => (
                    <li key={item} className="border-t border-white/8 pt-3 first:border-t-0 first:pt-0">
                      {item}
                    </li>
                  ))}
                </ul>
                <p className="mt-5 text-sm text-white/44">
                  {'Generic sales spam and outsourcing pitches will die lonely.'}
                </p>
              </div>
            </div>
          </section>
          <section className="contact-panel p-6 md:p-7">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-phosphor-ivory">{'Contact me'}</h2>
              <p className="mt-2 text-sm leading-7 text-white/64">{'Keep it specific.'}</p>
            </div>
            <form className="space-y-5" noValidate onSubmit={handleSubmit}>
              <div aria-hidden="true" className="absolute left-[-9999px] top-0">
                <label htmlFor="website">Website</label>
                <input
                  autoComplete="off"
                  id="website"
                  name="website"
                  tabIndex={-1}
                  type="text"
                  value={values.website}
                  onChange={(event) => handleFieldChange('website', event.target.value)}
                />
              </div>
              <ContactField error={fieldErrors.name} htmlFor="contact-name" label={'Name'}>
                <input
                  aria-describedby={fieldErrors.name ? 'contact-name-error' : undefined}
                  aria-invalid={Boolean(fieldErrors.name)}
                  autoComplete="name"
                  className="contact-input"
                  id="contact-name"
                  name="name"
                  placeholder={'Your name'}
                  type="text"
                  value={values.name}
                  onChange={(event) => handleFieldChange('name', event.target.value)}
                />
              </ContactField>
              <ContactField error={fieldErrors.email} htmlFor="contact-email" label={'Email'}>
                <input
                  aria-describedby={fieldErrors.email ? 'contact-email-error' : undefined}
                  aria-invalid={Boolean(fieldErrors.email)}
                  autoComplete="email"
                  className="contact-input"
                  id="contact-email"
                  name="email"
                  placeholder={'you@company.com'}
                  type="email"
                  value={values.email}
                  onChange={(event) => handleFieldChange('email', event.target.value)}
                />
              </ContactField>
              <ContactField error={fieldErrors.company} htmlFor="contact-company" label={'Company or project'}>
                <input
                  aria-describedby={fieldErrors.company ? 'contact-company-error' : undefined}
                  aria-invalid={Boolean(fieldErrors.company)}
                  autoComplete="organization"
                  className="contact-input"
                  id="contact-company"
                  name="company"
                  placeholder={'Optional'}
                  type="text"
                  value={values.company}
                  onChange={(event) => handleFieldChange('company', event.target.value)}
                />
              </ContactField>
              <ContactField error={fieldErrors.reason} htmlFor="contact-reason" label={'Reason'}>
                <select
                  aria-describedby={fieldErrors.reason ? 'contact-reason-error' : undefined}
                  aria-invalid={Boolean(fieldErrors.reason)}
                  className="contact-input"
                  id="contact-reason"
                  name="reason"
                  value={values.reason}
                  onChange={(event) => handleFieldChange('reason', event.target.value)}
                >
                  <option disabled value="">
                    {'Pick the least wrong option'}
                  </option>
                  {[
                    {
                      label: 'You need someone to un-fuck a codebase',
                      value: 'codebase-triage',
                    },
                    {
                      label: 'You want an audit before prod gets theatrical',
                      value: 'audit',
                    },
                    {
                      label: 'You need a builder, not a meeting attendee',
                      value: 'builder',
                    },
                    {
                      label: 'You want a sharp second brain on architecture',
                      value: 'architecture',
                    },
                    {
                      label: 'You have a weird problem and that sounds promising',
                      value: 'weird-problem',
                    },
                    {
                      label: 'Something else, but it is at least real',
                      value: 'other',
                    },
                  ].map((option) => (
                    <option key={option.value} value={option.label}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </ContactField>
              <ContactField error={fieldErrors.message} htmlFor="contact-message" label={'Message'}>
                <textarea
                  aria-describedby={fieldErrors.message ? 'contact-message-error' : undefined}
                  aria-invalid={Boolean(fieldErrors.message)}
                  className="contact-input min-h-44 resize-y"
                  id="contact-message"
                  name="message"
                  placeholder={'What is broken, what matters, and what do you want me to do?'}
                  value={values.message}
                  onChange={(event) => handleFieldChange('message', event.target.value)}
                />
              </ContactField>
              {formMessage ? (
                <p
                  className={`contact-feedback ${submissionState === 'success' ? 'contact-feedback-success' : 'contact-feedback-error'}`}
                  role={submissionState === 'success' ? 'status' : 'alert'}
                >
                  {formMessage}
                </p>
              ) : null}
              <Button className="w-full justify-center" disabled={isSubmitting} size="lg" type="submit">
                {isSubmitting ? 'Sending...' : 'Contact me'}
              </Button>
              <p className="text-xs leading-6 text-white/46">
                {'This goes straight to the inbox. No newsletter nonsense.'}
              </p>
            </form>
          </section>
        </div>
      </main>
    </div>
  )
}
